import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, CircularProgress, Alert, FormControl,
  InputLabel, Select, MenuItem, Grid, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Button, Tooltip
} from '@mui/material';
import { CompareArrows as CompareIcon } from '@mui/icons-material';
import api from '../../services/api';

const statusColor = { struggling: 'error', developing: 'warning', mastered: 'success' };

export default function ClassAnalyticsTab({ schoolId }) {
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [compareClassId, setCompareClassId] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [completionGrid, setCompletionGrid] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [error, setError] = useState(null);
  const [showComparison, setShowComparison] = useState(false);

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const res = await api.get(`/api/schools/${schoolId}/classes`);
        setClasses(res.data.data || res.data.classes || []);
      } catch {
        setError('Failed to load classes');
      } finally {
        setLoadingClasses(false);
      }
    };
    fetchClasses();
  }, [schoolId]);

  useEffect(() => {
    if (!selectedClassId) { setAnalytics(null); setCompletionGrid(null); return; }
    const fetchAnalytics = async () => {
      setLoading(true); setError(null);
      try {
        const [analyticsRes, gridRes] = await Promise.all([
          api.get(`/api/schools/${schoolId}/classes/${selectedClassId}/analytics`),
          api.get(`/api/schools/${schoolId}/classes/${selectedClassId}/completion-grid`)
        ]);
        setAnalytics(analyticsRes.data.data || analyticsRes.data);
        setCompletionGrid(gridRes.data.data || gridRes.data);
      } catch {
        setError('Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [schoolId, selectedClassId]);

  const handleCompare = async () => {
    if (!selectedClassId || !compareClassId) return;
    try {
      setLoading(true);
      const res = await api.get(`/api/schools/${schoolId}/compare-classes?classIdA=${selectedClassId}&classIdB=${compareClassId}`);
      setComparison(res.data.data || res.data);
    } catch {
      setError('Failed to compare classes');
    } finally {
      setLoading(false);
    }
  };

  if (loadingClasses) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

  // Safely extract arrays
  const topicAccuracy = Array.isArray(analytics?.topicAccuracy) ? analytics.topicAccuracy : [];
  const learnerTable = Array.isArray(analytics?.learnerTable) ? analytics.learnerTable : [];
  const diffDist = Array.isArray(analytics?.difficultyDistribution) ? analytics.difficultyDistribution : [];
  const grid = completionGrid?.grid || [];
  const gridTopics = Array.isArray(completionGrid?.topics) ? completionGrid.topics : [];

  return (
    <Box>
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Select Class</InputLabel>
          <Select value={selectedClassId} label="Select Class" onChange={e => { setSelectedClassId(e.target.value); setComparison(null); }}>
            {classes.map(c => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
          </Select>
        </FormControl>
        <Button size="small" variant="outlined" startIcon={<CompareIcon />} onClick={() => setShowComparison(!showComparison)}>Compare</Button>
        {showComparison && (
          <>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Compare With</InputLabel>
              <Select value={compareClassId} label="Compare With" onChange={e => setCompareClassId(e.target.value)}>
                {classes.filter(c => c._id !== selectedClassId).map(c => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
              </Select>
            </FormControl>
            <Button variant="contained" size="small" onClick={handleCompare} disabled={!compareClassId || loading}>Go</Button>
          </>
        )}
      </Box>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}

      {!loading && analytics && (
        <Grid container spacing={2}>
          {/* Topic Accuracy Chips */}
          {topicAccuracy.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Topic Accuracy ({topicAccuracy.length} topics)
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {topicAccuracy.map((t, i) => (
                    <Tooltip key={i} title={`${t.total} attempts by ${t.learnerCount} learners`}>
                      <Chip
                        label={`${t._id || t.topic}: ${t.accuracy}%`}
                        color={statusColor[t.status] || (t.accuracy >= 70 ? 'success' : t.accuracy >= 50 ? 'warning' : 'error')}
                        variant="outlined"
                        size="small"
                      />
                    </Tooltip>
                  ))}
                </Box>
              </Paper>
            </Grid>
          )}

          {/* Learner Performance Table */}
          {learnerTable.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Learner Performance ({learnerTable.length} learners)
                </Typography>
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Learner</TableCell>
                        <TableCell align="right">Questions</TableCell>
                        <TableCell align="right">Correct</TableCell>
                        <TableCell align="right">Accuracy</TableCell>
                        <TableCell align="right">Topics</TableCell>
                        <TableCell>Weakest</TableCell>
                        <TableCell>Strongest</TableCell>
                        <TableCell>Trend</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {learnerTable.map((l, i) => (
                        <TableRow key={l.userId || i} hover>
                          <TableCell>
                            <Typography variant="body2">{l.name || l.username}</Typography>
                            <Typography variant="caption" color="text.secondary">{l.username}</Typography>
                          </TableCell>
                          <TableCell align="right">{l.totalAnswered || 0}</TableCell>
                          <TableCell align="right">{l.totalCorrect || 0}</TableCell>
                          <TableCell align="right">
                            <Chip label={`${l.accuracy || 0}%`} size="small"
                              color={l.accuracy >= 70 ? 'success' : l.accuracy >= 50 ? 'warning' : 'error'} variant="outlined" />
                          </TableCell>
                          <TableCell align="right">{l.topicCount || 0}</TableCell>
                          <TableCell>
                            {l.weakestTopic && <Chip label={l.weakestTopic} size="small" color="error" variant="outlined" sx={{ fontSize: '0.65rem' }} />}
                          </TableCell>
                          <TableCell>
                            {l.strongestTopic && <Chip label={l.strongestTopic} size="small" color="success" variant="outlined" sx={{ fontSize: '0.65rem' }} />}
                          </TableCell>
                          <TableCell>
                            {l.trend && <Chip label={l.trend} size="small" variant="outlined"
                              color={l.trend === 'improving' ? 'success' : l.trend === 'declining' ? 'error' : 'default'}
                              sx={{ fontSize: '0.65rem' }} />}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          )}

          {/* Difficulty Distribution */}
          {diffDist.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Difficulty Breakdown by Topic</Typography>
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Topic</TableCell>
                        <TableCell align="center">Easy</TableCell>
                        <TableCell align="center">Medium</TableCell>
                        <TableCell align="center">Hard</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {diffDist.map((row, i) => {
                        const byDiff = {};
                        (row.difficulties || []).forEach(d => { byDiff[d.difficulty] = d; });
                        return (
                          <TableRow key={i} hover>
                            <TableCell>{row._id}</TableCell>
                            {['easy', 'medium', 'hard'].map(d => {
                              const v = byDiff[d];
                              if (!v) return <TableCell key={d} align="center">-</TableCell>;
                              const color = v.accuracy >= 70 ? '#c8e6c9' : v.accuracy >= 50 ? '#fff9c4' : '#ffcdd2';
                              return (
                                <TableCell key={d} align="center" sx={{ bgcolor: color }}>
                                  {v.accuracy}% ({v.total})
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          )}

          {/* Completion Grid Heatmap */}
          {grid.length > 0 && gridTopics.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2, overflow: 'auto' }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Topic Completion Grid
                </Typography>
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 3, minWidth: 120 }}>
                          Learner
                        </TableCell>
                        {gridTopics.map((t, i) => (
                          <TableCell key={i} align="center" sx={{ whiteSpace: 'nowrap', fontSize: '0.65rem', writingMode: 'vertical-rl', minWidth: 30, p: 0.5 }}>
                            {t}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {grid.map((row, i) => (
                        <TableRow key={i}>
                          <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1, whiteSpace: 'nowrap' }}>
                            {row.name || row.username}
                          </TableCell>
                          {gridTopics.map((t, j) => {
                            const cell = row.topics?.[t];
                            if (!cell || !cell.attempted) {
                              return <TableCell key={j} align="center" sx={{ bgcolor: '#f5f5f5', fontSize: '0.65rem', p: 0.5 }}>-</TableCell>;
                            }
                            const acc = cell.accuracy || 0;
                            const bg = acc >= 70 ? '#c8e6c9' : acc >= 40 ? '#fff9c4' : '#ffcdd2';
                            return (
                              <Tooltip key={j} title={`${cell.attempted} attempted, ${acc}% accuracy`}>
                                <TableCell align="center" sx={{ bgcolor: bg, fontSize: '0.65rem', p: 0.5, cursor: 'help' }}>
                                  {acc}%
                                </TableCell>
                              </Tooltip>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}

      {/* Comparison */}
      {comparison && (
        <Paper sx={{ mt: 2, p: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Class Comparison: {comparison.classA?.name} vs {comparison.classB?.name}
          </Typography>
          <Grid container spacing={2}>
            {['classA', 'classB'].map(key => (
              <Grid item xs={6} key={key}>
                <Typography variant="subtitle2" color="primary">{comparison[key]?.name}</Typography>
                <Typography variant="body2">Learners: {comparison[key]?.learnerCount}</Typography>
                <Typography variant="body2">Avg Accuracy: {comparison[key]?.averageAccuracy}%</Typography>
                <Typography variant="body2">Questions: {comparison[key]?.totalQuestions}</Typography>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {!selectedClassId && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Select a class above to view analytics.</Typography>
        </Paper>
      )}
    </Box>
  );
}
